import { createRouter, createWebHistory } from 'vue-router';
import store from '@/store';

const routes = [
  {
    path: '/',
    name: 'homePage',
    component: () => import('@/views/HomePage.vue'),
    meta: { requiresUser: true },
  },
  {
    path: '/about',
    name: 'aboutPage',
    component: () => import('@/views/AboutView.vue'),
    meta: { requiresUser: true },
  },
  {
    path: '/login',
    name: 'loginPage',
    component: () => import('@/views/LoginPage.vue'),
    meta: { requiresGuest: true },
  },

  {
    path: '/members',
    name: 'memberPage',
    component: () => import('@/views/MemberPage.vue'),
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'members',
        component: () => import('@/components/member/MemberList.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':memberId',
        name: 'memberDetails',
        component: () => import('@/components/member/MemberDetails.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },

  {
    path: '/me',
    name: 'mePage',
    component: () => import('@/views/MePage.vue'),
    meta: { requiresUser: true },
    children: [
      {
        path: '',
        name: 'me',
        component: () => import('@/components/me/MeDetails.vue'),
        meta: { requiresUser: true },
      },
      {
        path: 'products/new',
        name: 'meProductCreation',
        component: () => import('@/components/me/MeProductCreationForm.vue'),
        meta: { requiresUser: true },
      },
      {
        path: 'products/:teacherProductId',
        name: 'meProductUpdate',
        component: () => import('@/components/me/MeProductUpdateForm.vue'),
        meta: { requiresUser: true },
      },
    ],
  },

  {
    path: '/teachers',
    name: 'teacherPage',
    component: () => import('@/views/TeacherPage.vue'),
    meta: { requiresAdmin: true },
    children: [
      {
        path: 'sorts',
        name: 'teachersSortedList',
        component: () => import('@/components/teacher/TeacherSortedList.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: '',
        name: 'teachers',
        component: () => import('@/components/teacher/TeacherList.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'new',
        name: 'teacherCreation',
        component: () => import('@/components/teacher/TeacherCreationForm.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':teacherId',
        name: 'teacherDetails',
        component: () => import('@/components/teacher/TeacherDetails.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':teacherId/products/new',
        name: 'teacherProductCreation',
        component: () =>
          import('@/components/teacher/TeacherProductCreationForm.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':teacherId/products/:teacherProductId',
        name: 'teacherProductUpdate',
        component: () =>
          import('@/components/teacher/TeacherProductUpdateForm.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },

  {
    path: '/schedules',
    name: 'schedulePage',
    component: () => import('@/views/SchedulePage.vue'),
    meta: { requiresUser: true },
    children: [
      {
        path: '',
        name: 'schedules',
        component: () => import('@/components/schedule/ScheduleList.vue'),
        meta: { requiresUser: true },
      },
      {
        path: 'new',
        name: 'scheduleCreation',
        component: () => import('@/views/ScheduleCreationPage.vue'),
        meta: { requiresUser: true },
      },
    ],
  },

  {
    path: '/products',
    name: 'productPage',
    component: () => import('@/views/ProductPage.vue'),
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'products',
        component: () => import('@/components/product/ProductList.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'new',
        name: 'productCreation',
        component: () => import('@/components/product/ProductCreationForm.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':roadmapId',
        name: 'productUpdate',
        component: () => import('@/components/product/ProductUpdateForm.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },

  {
    path: '/consultations',
    name: 'consultationPage',
    component: () => import('@/views/ConsultationPage.vue'),
    meta: { requiresUser: true },
    children: [
      {
        path: '',
        name: 'consultations',
        component: () =>
          import('@/components/consultation/ConsultationList.vue'),
        meta: { requiresUser: true },
      },
      {
        path: ':payRecordId/details',
        name: 'consultationDetails',
        component: () =>
          import('@/components/consultation/ConsultationDetails.vue'),
        meta: { requiresUser: true },
      },
    ],
  },

  {
    path: '/qna',
    name: 'qnaPage',
    component: () => import('@/views/QnaPage.vue'),
    meta: { requiresUser: true },
    children: [
      {
        path: '',
        name: 'qna',
        component: () => import('@/components/qna/QnaList.vue'),
        meta: { requiresUser: true },
      },
    ],
  },

  {
    path: '/payments',
    name: 'paymentPage',
    component: () => import('@/views/PaymentPage.vue'),
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'payments',
        component: () => import('@/components/payment/PaymentList.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':payMasterId/details',
        name: 'paymentsDetails',
        component: () => import('@/components/payment/PaymentDetails.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'new',
        name: 'paymentsCreation',
        component: () => import('@/components/payment/PaymentCreationForm.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'settlements',
        name: 'settlementList',
        component: () => import('@/components/payment/SettlementList.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },

  {
    path: '/carts',
    name: 'cartPage',
    component: () => import('@/views/CartPage.vue'),
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'carts',
        component: () => import('@/components/cart/CartList.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'new',
        name: 'cartCreation',
        component: () => import('@/components/cart/CartCreationForm.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':cartId/details',
        name: 'cartUpdate',
        component: () => import('@/components/cart/CartUpdateForm.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },

  {
    path: '/messages',
    name: 'messagePage',
    component: () => import('@/views/MessagePage.vue'),
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'messageList',
        component: () => import('@/components/message/MessageList.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'new',
        name: 'messageForm',
        component: () => import('@/components/message/MessageForm.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },

  {
    path: '/coupons',
    name: 'couponPage',
    component: () => import('@/views/CouponPage.vue'),
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'coupons',
        component: () => import('@/components/coupon/CouponList.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: 'new',
        name: 'couponCreateForm',
        component: () => import('@/components/coupon/CouponCreateForm.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':couponId/details',
        name: 'couponDetails',
        component: () => import('@/components/coupon/CouponDetails.vue'),
        meta: { requiresAdmin: true },
      },
      {
        path: ':couponId/update',
        name: 'couponUpdateForm',
        component: () => import('@/components/coupon/CouponUpdateForm.vue'),
        meta: { requiresAdmin: true },
      },

      {
        path: ':couponId/claims/new',
        name: 'couponClaimForm',
        component: () => import('@/components/coupon/CouponClaimForm.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },

  {
    path: '/reviews',
    name: 'reviewPage',
    component: () => import('@/views/ReviewPage.vue'),
    meta: { requiresAdmin: true },
    children: [
      {
        path: '',
        name: 'reviews',
        component: () => import('@/components/review/ReviewList.vue'),
        meta: { requiresAdmin: true },
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const isAuthenticated = store.getters.isAuthenticated;
  const isAdmin = store.getters.isAdmin;
  document.title = '결정적 이순간 | 컨설턴트 플랫폼';

  if (to.matched.some(record => record.meta.requiresGuest) && isAuthenticated) {
    alert('이미 로그인된 상태입니다.');
    next({ path: '/' });
    return;
  }

  if (to.matched.some(record => record.meta.requiresUser) && !isAuthenticated) {
    next({ path: '/login' });
    return;
  }

  if (to.matched.some(record => record.meta.requiresAdmin) && !isAdmin) {
    alert('관리자 권한이 필요합니다.');
    next({ path: '/' });
    return;
  }

  next();
});

export default router;
